import React, {useState} from 'react';
import {Tabs} from 'antd-mobile';
import {Table} from 'antd';
import { t } from '../../../i18n';
import { formatLocalizedTime } from '../../../utils/dateFormatUtils';
import { useGetCommonConfigByKey } from '../../../graphql/queries/commonConfigQueries';
import { getUserInfo } from '../../../utils/auth';

// 配置键：PDA扫描结果表最大记录数
const CONFIG_KEY_MAX_RECORDS = 'pda.scan_result_table.max_records';

// 配置键：PDA扫描结果表最大记录数限制手机号白名单
const CONFIG_KEY_MAX_RECORDS_PHONE_WHITELIST = 'pda.scan_result_table.max_records.phone_whitelist';

/**
 * 自定义 Hook：获取当前用户的最大记录数配置
 * @returns 最大记录数，如果不满足限制条件则返回 Number.MAX_SAFE_INTEGER
 */
export const useMaxRecords = (): number => {
    const { data: whitelistData } = useGetCommonConfigByKey(CONFIG_KEY_MAX_RECORDS_PHONE_WHITELIST);
    const { data: maxRecordsData } = useGetCommonConfigByKey(CONFIG_KEY_MAX_RECORDS);

    // 检查白名单配置是否激活
    const whitelistConfig = whitelistData?.getCommonConfigByKey;
    if (!whitelistConfig || !whitelistConfig.isActive) {
        return Number.MAX_SAFE_INTEGER;
    }

    // 检查当前用户是否在白名单中
    const checkPhoneInWhitelist = (whitelistConfigValue: string | null | undefined): boolean => {
        if (!whitelistConfigValue || whitelistConfigValue.trim() === '') {
            return false;
        }

        const userInfo = getUserInfo();
        const userPhone = userInfo?.account.phone;

        if (!userPhone) {
            return false;
        }

        // 解析白名单：去除空格，按逗号分割
        const whitelist = whitelistConfigValue.split(',').map(p => p.trim()).filter(p => p !== '');

        return whitelist.includes(userPhone);
    };

    const isInWhitelist = checkPhoneInWhitelist(whitelistConfig.configValue);

    // 如果用户不在白名单中，不做限制
    if (!isInWhitelist) {
        return Number.MAX_SAFE_INTEGER;
    }

    // 检查最大记录数配置是否激活
    const maxRecordsConfig = maxRecordsData?.getCommonConfigByKey;
    if (!maxRecordsConfig || !maxRecordsConfig.isActive) {
        return Number.MAX_SAFE_INTEGER;
    }

    // 解析最大记录数配置值
    const configValue = maxRecordsConfig.configValue;
    if (!configValue) {
        return Number.MAX_SAFE_INTEGER;
    }

    const parsedValue = parseInt(configValue, 10);
    return (!isNaN(parsedValue) && parsedValue > 0) ? parsedValue : Number.MAX_SAFE_INTEGER;
};

export type ScanRecord = {
    id?: string;
    value1?: string;
    value2?: string;
    time?: string;
    failedReason?: string;
}

export const createRecord = (record: ScanRecord) : ScanRecord => {
    return { id: Date.now().toString() + '-' + Math.random().toString(36).slice(2, 11), time: formatLocalizedTime(), ...record};
}

/**
 * 添加新记录并限制数组大小
 * @param newRecord 新记录
 * @param existingRecords 现有记录数组
 * @param maxSize 最大记录数，默认不限制
 * @returns 限制大小后的新数组
 */
export const addRecordWithLimit = (newRecord: ScanRecord, existingRecords: ScanRecord[], maxSize: number = Number.MAX_SAFE_INTEGER): ScanRecord[] => {
    const newRecords = [newRecord, ...existingRecords];
    return newRecords.slice(0, maxSize);
}

export interface Column {
    title: string;
    key: string;
}

export interface ResultTableProps {
    successRecords: ScanRecord[];
    failedRecords: ScanRecord[];
    successColumns: Column[];
    failedColumns: Column[];
    totalSuccessCount?: number;  // 总成功数（不受显示限制）
    totalFailedCount?: number;   // 总失败数（不受显示限制）
    onSuccessRowClick?: (record: ScanRecord) => void;
    successClickableKey?: string;
}

export const ResultTable: React.FC<ResultTableProps> = ({
                                                            successRecords,
                                                            failedRecords,
                                                            successColumns,
                                                            failedColumns,
                                                            totalSuccessCount,
                                                            totalFailedCount,
                                                            onSuccessRowClick,
                                                            successClickableKey,
                                                        }) => {
    const [activeTab, setActiveTab] = useState('success');

    // 使用传入的总数，如果没有传入则使用数组长度（向后兼容）
    const successCount = totalSuccessCount ?? successRecords.length;
    const failedCount = totalFailedCount ?? failedRecords.length;

    // 准备成功记录的数据
    const successData = successRecords.map(record => ({
        key: record.id,
        ...record,
    }));

    // 准备失败记录的数据（包含失败原因）
    const failedData = failedRecords.map(record => ({
        key: record.id,
        ...record,
    }));

    return (
        <>
            {/* 选项卡 */}
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                className="border-b"
            >
                <Tabs.Tab title={`${t('pda.common.succeed')}(${successCount})`} key="success"/>
                <Tabs.Tab title={`${t('pda.common.failed')}(${failedCount})`} key="failed"/>
            </Tabs>

            {/* 数据列表 */}
            <div className="flex-1 overflow-auto">
                {activeTab === 'success' ? (
                    successRecords.length > 0 ? (
                        <Table
                            columns={successColumns.map(column => ({
                                title: column.title,
                                dataIndex: column.key,
                                key: column.key,
                                onHeaderCell: () => ({
                                    className: 'whitespace-normal break-words'
                                }),
                                render: (text: string, record: any, index: number) => {
                                    let displayText = text;
                                    if (column.key === 'nextAction' && text) {
                                        displayText = t(`pda.common.exceptionActions.${text}`);
                                    }

                                    if (onSuccessRowClick && column.key === successClickableKey) {
                                        return (
                                            <span
                                                style={{ color: '#1677ff', textDecoration: 'underline', cursor: 'pointer' }}
                                                onClick={() => onSuccessRowClick(record as ScanRecord)}
                                            >
                                                {displayText}
                                            </span>
                                        );
                                    }

                                    return (
                                        <div className="whitespace-normal break-words break-all">
                                            {displayText}
                                        </div>
                                    );
                                },
                            }))}
                            dataSource={successData}
                            size="small"
                        />
                    ) : (
                        <div className="p-4 text-center text-gray-500">{t('pda.common.noRecords')}</div>
                    )
                ) : (
                    failedRecords.length > 0 ? (
                        <Table
                            columns={failedColumns.map(column => ({
                                title: column.title,
                                dataIndex: column.key,
                                key: column.key,
                                onHeaderCell: () => ({
                                    className: 'whitespace-normal break-words'
                                }),
                                render: (text: string, record: any, index: number) => {
                                    // 对nextAction字段进行国际化处理
                                    let displayText = text;
                                    if (column.key === 'nextAction' && text) {
                                        displayText = t(`pda.common.exceptionActions.${text}`);
                                    }

                                    return (
                                        <div className="whitespace-normal break-words break-all">
                                            {displayText}
                                        </div>
                                    );
                                },
                            }))}
                            dataSource={failedData}
                            size="small"
                        />
                    ) : (
                        <div className="p-4 text-center text-gray-500">{t('pda.common.noRecords')}</div>
                    )
                )}
            </div>
        </>
    )
}